import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Sparkles } from "lucide-react";
import { SideNav, MobileNav } from "@/components/nav/MainNav";
import { WhatsNew } from "@/components/hub/WhatsNew";
import { AssistBubble } from "@/components/assist/AssistBubble";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Logo, LogoWide } from "@/components/Logo";
import { BackButton } from "@/components/BackButton";
import { Footer } from "@/components/Footer";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { HolidayBadge } from "@/components/holiday/HolidaySkin";
import { LimitOfferDialog } from "@/components/LimitOffer";
import { WinbackToast } from "@/components/WinbackToast";
import { useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const usePendingGift = (user, t) => {
  const nav = useNavigate();
  useEffect(() => {
    const code = localStorage.getItem("clipx_gift");
    if (!user || !code) return;
    localStorage.removeItem("clipx_gift");
    api.post("/promo/redeem", { code }).then((r) => {
      toast.success(t("gift.ok").replace("{n}", r.data.clips), { duration: 10000, id: "gift-ok" });
      nav("/credits?gift=1");
    })
      .catch((e) => { if (!(e.response?.status >= 400 && e.response?.status < 500)) localStorage.setItem("clipx_gift", code); toast.error(formatApiError(e)); });
  }, [user, t]);
};

const useYtReturn = (t) => {
  const { search, pathname } = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    const q = new URLSearchParams(search);
    const r = q.get("yt");
    if (!r) return;
    if (r === "connected") toast.success(t("pub.connected"), { id: "yt-return" }); else toast.error(t(`pub.r_${r}`), { id: "yt-return" });
    q.delete("yt");
    nav({ pathname, search: q.toString() }, { replace: true });
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const Layout = ({ children }) => {
  const { user: gu } = useAuth();
  const { t: gt } = useI18n();
  usePendingGift(gu, gt);
  useYtReturn(gt);
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen md:flex cx-grid-bg">
      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between gap-3 px-4 py-3 bg-[#06070e]/95 backdrop-blur-xl border-b border-white/10" data-testid="mobile-header">
        <button onClick={() => nav("/dashboard")} data-testid="mobile-logo" className="relative w-36"><LogoWide /><HolidayBadge className="-top-1 -right-3" /></button>
        <div className="flex items-center gap-2">
          <LanguageSwitcher compact />
          <button data-testid="mobile-logout-btn" onClick={logout} className="cx-ghost rounded-lg p-2.5"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>

      <aside className="hidden md:flex w-[84px] lg:w-64 shrink-0 overflow-y-auto cx-scroll border-r border-white/5 bg-[#06070e]/90 backdrop-blur-xl flex-col sticky top-0 h-screen z-30">
        <div className="relative px-4 lg:px-5 py-5 cursor-pointer flex justify-center lg:justify-start" onClick={() => nav("/dashboard")} data-testid="sidebar-logo">
          <HolidayBadge className="top-3 right-3" />
          <div className="lg:hidden"><Logo size={44} /></div>
          <LogoWide className="hidden lg:block w-full" />
        </div>
        <SideNav t={t} user={user} />
        <div className="mx-3 mt-8 p-4 rounded-xl border border-[#a746ff]/25 bg-[#a746ff]/[0.06] hidden lg:block">
          <div className="flex items-center gap-2 text-[#c79bff] text-xs font-mono uppercase tracking-widest"><Sparkles className="w-3.5 h-3.5" /> {t("nav.ai_active")}</div>
          <p className="text-xs text-slate-400 mt-2 leading-relaxed">{t("nav.ai_text")}</p>
        </div>
        <div className="mt-auto p-3 lg:p-4 border-t border-white/5 space-y-3">
          <div className="hidden lg:block"><LanguageSwitcher /></div>
          <div className="lg:hidden flex justify-center"><LanguageSwitcher compact /></div>
          <div className="hidden lg:block" data-testid="workspace-badge">
            <div className="cx-label">{t("nav.workspace_label")}</div>
            <div className="font-display text-sm text-white mt-1 truncate">{user?.workspace?.name}</div>
            <div className="font-mono text-[11px] text-slate-500 truncate">ID: {user?.tenant_id}</div>
            <div className="text-xs text-slate-400 mt-1 truncate">{user?.email}</div>
          </div>
          <button data-testid="logout-btn" onClick={logout} title={t("nav.logout")} className="cx-ghost w-full rounded-lg px-3 py-2.5 text-sm text-slate-300 flex items-center justify-center lg:justify-start gap-2">
            <LogOut className="w-4 h-4" /> <span className="hidden lg:inline">{t("nav.logout")}</span>
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 pb-24 md:pb-0 flex flex-col min-h-screen">
        <div className="px-4 sm:px-6 lg:px-10 2xl:px-14 pt-4 sm:pt-6 -mb-2 min-h-[50px] sm:min-h-[58px]">{!pathname.startsWith("/editor") && <BackButton />}</div>
        <div key={pathname} className="flex-1 cx-page-in">{children}</div><Footer />
      </main>
      <MobileNav t={t} user={user} />
      <WhatsNew />
      <LimitOfferDialog />
      <WinbackToast />
      <AssistBubble />
    </div>
  );
};
